import {
  Chart,
  ChartBody,
  ChartContainer,
  ChartFooter,
  DateRange,
  ReferenceArea,
  SeriesType,
  ValueRange,
} from 'bold-ui/lib/components/Chart'
import { TickProps } from 'bold-ui/lib/components/Chart/Tick'
import { gray, green, red } from 'bold-ui/lib/styles/colors'
import { SexoEnum } from 'graphql/types.generated'
import moment, { Duration, Moment } from 'moment'
import { useCallback } from 'react'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { reduceMedicoesIguais } from '../../util-medicoes'
import { getReferenceAreasCalculators, ReferenceAreasCalculator } from './referenceAreaCalculators'
import { TickIdade } from './TickIdade'
import { TooltipGraficoAntropometria } from './TooltipGraficoAntropometria'
import { buildAreasPreTermo, dataNascimentoPreTermo, PeriodoPreTermo } from './util-grafico-pre-termo'
export interface GraficoAlturaProps {
  medicoes: HistoricoMedicaoModel[]
  dataNascimento: Moment
  dataRange: DateRange
  sexo: SexoEnum
  periodoPreTermo?: Readonly<PeriodoPreTermo>
  shouldRenderPreTermo?: boolean
}

// TODO@CODEBENDERS - Adicionar funcionalidade de outliers #23674
export function GraficoAlturaView(props: Readonly<GraficoAlturaProps>) {
  const { medicoes, dataNascimento, dataRange, sexo, periodoPreTermo, shouldRenderPreTermo = false } = props

  const dataNascimentoAjustada =
    shouldRenderPreTermo && periodoPreTermo ? dataNascimentoPreTermo(periodoPreTermo) : moment(dataNascimento)
  const maxIdadeNoRange = moment.duration(moment(dataRange.end).diff(dataNascimentoAjustada))
  const medicoesComAltura = reduceMedicoesIguais(medicoes, 'valorAltura') ?? []
  const seriesData = medicoesComAltura.map((m) => ({
    x: m.dataMedicao,
    y: m.valorAltura,
  }))
  const yRange = shouldRenderPreTermo ? yRangePreTermo(sexo) : getYRangePadrao(maxIdadeNoRange)

  const renderRangeAreas = () => {
    return !shouldRenderPreTermo && periodoPreTermo ? buildAreasPreTermo(periodoPreTermo, dataRange) : []
  }

  const tickRenderer = useCallback(
    (tickProps: TickProps) => (
      <TickIdade {...tickProps} dataNascimento={dataNascimentoAjustada} shouldRenderPreTermo={shouldRenderPreTermo} />
    ),
    [dataNascimentoAjustada, shouldRenderPreTermo]
  )

  const tituloEixoX = shouldRenderPreTermo ? 'Semanas' : 'Idade (meses completos e anos)'
  const tituloEixoY = maxIdadeNoRange.years() <= 2 ? 'Comprimento' : 'Altura'

  return (
    <ChartContainer>
      <ChartBody height={500}>
        <Chart<Moment>
          outliers='auto'
          type={SeriesType.Area}
          series={[{ name: 'Altura', data: seriesData }]}
          referenceAreas={getReferenceAreas(
            sexo,
            dataRange,
            maxIdadeNoRange,
            dataNascimentoAjustada,
            shouldRenderPreTermo
          )}
          xAxis={{
            title: tituloEixoX,
            domain: dataRange,
            tickRenderer,
          }}
          rangeAreas={renderRangeAreas()}
          yAxis={{
            title: tituloEixoY,
            unit: 'cm',
            domain: yRange,
          }}
          showLegend={false}
          tooltip={{
            type: 'point',
            render: (points) => (
              <TooltipGraficoAntropometria
                label={tituloEixoY}
                unidadeDeMedida='cm'
                points={points}
                dataNascimento={dataNascimento}
                shouldRenderPreTermo={shouldRenderPreTermo}
              />
            ),
          }}
        />
      </ChartBody>
      <ChartFooter>{getFooterText(maxIdadeNoRange, shouldRenderPreTermo)}</ChartFooter>
    </ChartContainer>
  )
}

function getFooterText(maxIdadeNoRange: Duration, shouldRenderPreTermo: boolean): string {
  if (shouldRenderPreTermo) {
    return 'Fonte: INTERGROWTH-21ˢᵗ - 2026.'
  }

  if (maxIdadeNoRange.years() <= 19) {
    return 'Fonte: Adaptado do Guia para a Organização da Vigilância Alimentar e Nutricional na Atenção Primária à Saúde, 2023.'
  }

  return ''
}

const yRangePreTermo = (sexo: SexoEnum): ValueRange => {
  if (sexo === SexoEnum.MASCULINO) return { init: 26, end: 76, step: 2 }
  return { init: 24, end: 74, step: 2 }
}

function getYRangePadrao(maxIdadeNoRange: Duration): ValueRange {
  if (maxIdadeNoRange.years() <= 2) return { init: 40, end: 105, step: 5 }
  else if (maxIdadeNoRange.years() <= 5) return { init: 75, end: 130, step: 5 }
  else if (maxIdadeNoRange.years() <= 10) return { init: 90, end: 165, step: 5 }
  else if (maxIdadeNoRange.years() <= 15) return { init: 100, end: 205, step: 5 }
  else if (maxIdadeNoRange.years() <= 19) return { init: 110, end: 210, step: 10 }
  else return { init: 110, end: 200, step: 5 }
}

function getReferenceAreas(
  sexo: SexoEnum,
  xRange: DateRange,
  idadeNoRange: Duration,
  dataNascimento: Moment,
  shouldRenderPreTermo: boolean
): ReferenceArea<Moment>[] {
  const refAreaCalculators = getReferenceAreasCalculators(sexo, dataNascimento, xRange, 'altura', shouldRenderPreTermo)

  if (shouldRenderPreTermo) {
    return [
      getReferenceAreaPreTermoRiscoInferior(refAreaCalculators),
      getReferenceAreaPreTermoAdequado(refAreaCalculators),
      getReferenceAreaPreTermoRiscoSuperior(refAreaCalculators),
    ]
  }

  if (idadeNoRange.years() <= 19) {
    return [
      getReferenceAreaMuitoBaixo(refAreaCalculators),
      getReferenceAreaBaixo(refAreaCalculators),
      getReferenceAreaAdequado(refAreaCalculators),
    ]
  }
  return undefined
}

function getReferenceAreaMuitoBaixo(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-3),
    })),
    label: {
      name: 'Muito baixo',
    },
    color: gray.c90,
    stroke: {
      color: gray.c40,
    },
    tick: {
      color: gray.c40,
    },
  }
}

function getReferenceAreaBaixo(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-2),
    })),
    label: {
      name: 'Baixo',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdequado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
    })),
    label: {
      name: 'Adequado',
    },
    color: green.c90,
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaPreTermoRiscoInferior(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-2),
    })),
    label: { name: 'Risco', color: red.c50 },
    color: red.c90,
    stroke: { color: red.c60 },
    tick: { color: red.c60 },
  }
}

function getReferenceAreaPreTermoAdequado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(2),
    })),
    label: { name: 'Adequado', color: green.c40 },
    color: green.c90,
    stroke: { color: red.c60 },
    tick: { color: green.c60 },
  }
}

function getReferenceAreaPreTermoRiscoSuperior(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
    })),
    // Se houver dois ReferenceAreas com a mesma label, o grafico se desconfigura.
    // Usamos um caractere invisivel (U+200B) para diferenciar, pois a label oficial
    // nao pode ser alterada. Isso sera resolvido na issue #31237.
    label: { name: 'Risco\u200B', color: red.c50 },
    color: red.c90,
    stroke: { color: 'none' },
    tick: { color: red.c60 },
  }
}
